/**
 * Other sites panel (listing-marketing-hub US-6, US-7, BR-9, BR-10, BR-12): a copy kit (title,
 * description with the public link and Resida email, the link alone, the email alone) from the
 * generic rendering, "Download all photos" as one zip in display order, and a note that posts on
 * other sites are not recorded (NG-7) — so there is no record form here. Instruction text is kept in
 * step with the market-on-other-sites Help article.
 */
import { useState } from 'react';
import { Download } from 'lucide-react';
import type { UnitPhoto } from '../../../types/unit-marketing.types';
import type { ListingMarketingExportDto } from '../../../lib/listing-marketing.api';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { CopyValueBox } from '../../../pages/leasing/listing-detail/ShareCopyButtons';
import { GuideLastChecked, HelpGuideLink } from './HelpGuideLink';
import { PostingControls, StepList } from './StepList';

export const OTHER_SITES_GUIDE_ARTICLE_ID = 'market-on-other-sites';
const THUMBNAILS_SHOWN = 4;

interface OtherSitesPanelProps {
  /** DC-7 copy kit; undefined while loading. */
  kit: ListingMarketingExportDto | undefined;
  kitError: boolean;
  onRetryKit: () => void;
  /** The unit's approved photos, in display order; undefined while loading. */
  photos: UnitPhoto[] | undefined;
  /** DC-6: fetches the photo archive and saves it; rejects when the archive request fails. */
  onDownloadPhotos: () => Promise<void>;
  /** The listing is paused or closed: copying and downloading are off, the guide stays readable (BR-1). */
  postingOff: boolean;
}

export function OtherSitesPanel({ kit, kitError, onRetryKit, photos, onDownloadPhotos, postingOff }: OtherSitesPanelProps) {
  const [downloadState, setDownloadState] = useState<'idle' | 'pending' | 'failed'>('idle');
  const count = photos?.length ?? 0;
  const thumbnails = (photos ?? []).filter((p) => p.thumbnailUrl).slice(0, THUMBNAILS_SHOWN);
  const more = count - thumbnails.length;

  const download = () => {
    setDownloadState('pending');
    onDownloadPhotos().then(
      () => setDownloadState('idle'),
      () => setDownloadState('failed'),
    );
  };

  return (
    <section
      aria-labelledby="marketing-other-title"
      data-testid="marketing-panel-other"
      className="flex min-w-0 flex-1 flex-col gap-5 rounded-xl border border-border bg-card p-[var(--card-padding)]"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h2 id="marketing-other-title" className="text-lg font-semibold text-foreground">
            Other sites
          </h2>
          <p className="text-sm text-muted-foreground">Copy the text, download the photos, post anywhere.</p>
        </div>
        <HelpGuideLink articleId={OTHER_SITES_GUIDE_ARTICLE_ID} testId="other-guide-link" />
      </div>

      <StepList
        testId="other-steps"
        steps={[
          {
            title: 'Copy the listing text',
            children: kitError ? (
              <div
                role="alert"
                data-testid="other-kit-error"
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
              >
                <p className="text-destructive">Couldn&apos;t load the listing text.</p>
                <Button type="button" variant="outline" onClick={onRetryKit} data-testid="other-kit-retry">
                  Retry
                </Button>
              </div>
            ) : !kit ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <PostingControls off={postingOff}>
                <CopyValueBox label="Title" value={kit.generic.title} emptyText="" testIdPrefix="other-title" />
                <CopyValueBox
                  label="Description"
                  value={kit.generic.description}
                  emptyText=""
                  testIdPrefix="other-description"
                  multiline
                  copyLabel="Copy description"
                  caption="Includes the link to your Resida listing page and your Resida email."
                />
                <div className="flex flex-col gap-3">
                  <CopyValueBox label="Listing link" value={kit.publicListingUrl} emptyText="" testIdPrefix="other-link" />
                  <CopyValueBox
                    label="Resida email"
                    value={kit.contactEmail}
                    emptyText="No Resida email for this listing"
                    testIdPrefix="other-email"
                  />
                </div>
              </PostingControls>
            ),
          },
          {
            title: 'Download the photos',
            children: !photos ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <>
                <p className="text-sm text-muted-foreground" data-testid="other-photo-count">
                  {count === 0
                    ? 'This listing has no approved photos yet.'
                    : `${count} ${count === 1 ? 'photo' : 'photos'}, in the order shown on your listing, numbered so the order survives upload.`}
                </p>
                {thumbnails.length > 0 && (
                  <div aria-hidden="true" className="flex flex-wrap gap-2">
                    {thumbnails.map((p) => (
                      <img
                        key={p.id}
                        src={p.thumbnailUrl ?? undefined}
                        alt=""
                        className="h-14 w-14 shrink-0 rounded-md border border-border object-cover"
                      />
                    ))}
                    {more > 0 && (
                      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-sm font-medium text-muted-foreground">
                        +{more}
                      </span>
                    )}
                  </div>
                )}
                <PostingControls off={postingOff}>
                  {count > 0 ? (
                    <div className="flex flex-col items-start gap-2">
                      <Button
                        type="button"
                        onClick={download}
                        disabled={downloadState === 'pending'}
                        data-testid="other-download-photos"
                        className="min-h-[44px] w-full sm:w-auto"
                      >
                        <Download aria-hidden="true" />
                        {downloadState === 'pending' ? 'Preparing photos…' : 'Download all photos'}
                      </Button>
                      {downloadState === 'failed' && (
                        <div
                          role="alert"
                          data-testid="other-download-error"
                          className="flex w-full flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
                        >
                          <p className="text-destructive">Couldn&apos;t download the photos.</p>
                          <Button type="button" variant="outline" onClick={download} data-testid="other-download-retry">
                            Retry
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-start gap-1.5">
                      <Button
                        type="button"
                        disabled
                        aria-describedby="other-download-photos-reason"
                        data-testid="other-download-photos"
                        className="min-h-[44px] w-full sm:w-auto"
                      >
                        <Download aria-hidden="true" />
                        Download all photos
                      </Button>
                      <p id="other-download-photos-reason" className="text-xs text-muted-foreground" data-testid="other-download-photos-reason">
                        Add photos in listing setup first.
                      </p>
                    </div>
                  )}
                </PostingControls>
                {count > 0 && (
                  <p className="text-xs text-muted-foreground">
                    On a phone, the download opens in your Files app; save the photos to your camera roll from there.
                  </p>
                )}
              </>
            ),
          },
          {
            title: 'Keep track yourself',
            children: (
              <p className="text-sm text-muted-foreground" data-testid="other-keep-track">
                Resida does not track posts on other sites. When this listing is paused or closed, take those posts
                down yourself.
              </p>
            ),
          },
        ]}
      />

      <GuideLastChecked articleId={OTHER_SITES_GUIDE_ARTICLE_ID} testId="other-guide-checked" />
    </section>
  );
}
